import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import useCommonGetInit from "../../events/hooks/useCommonGetInit";
import MuPb from "../../../widgets/MuPb";
import { getErrorResponse } from "../../../utils2024/utils";
import BackBtn from "../admin/widgets/BackBtn";

// Styled components for banner and profile image
const BannerContainer = styled(Box)(({ theme, banner }) => ({
  position: "relative",
  height: 200,
  backgroundColor: theme.palette.grey[300],
  backgroundImage: banner ? `url(${banner})` : "none",
  backgroundSize: "cover",
  backgroundPosition: "center",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  marginTop: -50,
  marginLeft: theme.spacing(2),
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  border: `4px solid ${theme.palette.background.paper}`,
}));

const VisitorProfileDetail = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error } = useCommonGetInit(
    `user/get-user-by-id?id=${id}`
  );

  if (isLoading) {
    return <MuPb />;
  }

  if (isError || !data) {
    return (
      <Alert severity="error">
        {error ? getErrorResponse(error) : "Failed to load visitor data"}
      </Alert>
    );
  }

  return (
    <Box>
      <BackBtn />
      <Box sx={{ maxWidth: 800, mx: "auto", my: 2 }}>
        <Card elevation={3}>
          <BannerContainer banner={data.profileBanner} />
          <ProfileImageContainer>
            <ProfileAvatar src={data.profileImage} alt={data.firstName}>
              {data.firstName?.charAt(0)}
              {data.lastName?.charAt(0)}
            </ProfileAvatar>
          </ProfileImageContainer>

          <CardContent>
            <Typography variant="h5" component="div" sx={{ mb: 2 }}>
              {data.title} {data.firstName} {data.lastName}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1">{data.username}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{data.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mobile No
                </Typography>
                <Typography variant="body1">{data.mobileNo}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company Name
                </Typography>
                <Typography variant="body1">{data.companyName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Visitor Group
                </Typography>
                <Typography variant="body1">{data.visitorGroup}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Industry Sector
                </Typography>
                <Typography variant="body1">{data.industrySector}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Location
                </Typography>
                <Typography variant="body1">
                  {data.city.name}, {data.state.name}, {data.country.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  How did you find out about this event?
                </Typography>
                <Typography variant="body1">
                  {data.howDidYouFindOutAboutThisEvent}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Product Categories
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {data.productCategories?.map((category, index) => (
                    <Chip
                      key={index}
                      label={`${category.MainCategory}: ${category.Category}`}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  QR Code
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <img
                    src={data.qrLink}
                    alt="QR Code"
                    style={{ maxWidth: 150, height: "auto" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VisitorProfileDetail;
